// =====================================================================
// Is this tab running a bundle the deploy has since replaced?
//
// A cockpit tab left open across a deploy keeps running the code it loaded, for as
// long as it stays open — which for the tab that matters is days. Every client-side
// fix therefore reaches the forgotten tab LAST, and the only lever today is asking
// SDRs to refresh, which is exactly the population least likely to be reading Slack.
//
// The deploy publishes `/version.json` next to `index.html` under the same never-cached
// headers; this compares it to the version inlined at build time. Deliberately a poll of a
// static file rather than a frame over the BFF socket: the socket would couple the web
// version to a BFF env var, and a web deploy without a BFF redeploy would silently stop
// reporting drift.
//
// EVERY UNCERTAIN CASE SAYS "NOT STALE". A false banner trains SDRs to ignore it, and
// the CloudFront SPA fallback means a missing file can answer with index.html rather
// than a 404 — so anything that isn't a well-formed sha different from ours is treated
// as "no news".
// =====================================================================

// The sha inlined by `vite build` in the deploy workflow. Absent in dev/local/test,
// which is what keeps the check inert there — there is nothing to be stale against.
//
// Empty counts as absent. Vite substitutes env values as strings, so a defined-but-empty
// VITE_GIT_SHA would survive a `?? null` as '' — non-null, and therefore unequal to every
// published sha, giving the whole fleet a banner no reload can clear. The published side
// already rejects '' below; this keeps the two ends symmetric.
const RAW_BUILD_SHA = (import.meta.env.VITE_GIT_SHA as string | undefined)?.trim();
export const BUILD_SHA = RAW_BUILD_SHA != null && RAW_BUILD_SHA !== '' ? RAW_BUILD_SHA : null;

// The SPA's own version: a commit count over the inputs that actually build this bundle,
// NOT the deploy commit. Most deploys touch neither, so they publish the same number and
// nobody is told anything.
//
// Deliberately separate from VITE_GIT_SHA, which stays the deploy commit because RUM tags
// every error with it — a version that appears in no deploy run would be untraceable.
export const BUILD_N = toCount((import.meta.env.VITE_WEB_VERSION as string | undefined) ?? null);

// How urgently the deploy wants this bundle picked up. `routine` is the default and says
// nothing; `wanted` is opt-in, per deploy, and is the only tier that interrupts.
export type ReloadTier = 'routine' | 'wanted';

// A stale `routine` tab still gets the banner once it has been stale this long. Without it
// a tab left open for days never hears about anything, which is the case the whole check
// was written for.
export const STALE_AGE_LIMIT_MS = 24 * 60 * 60_000;

// Every banner waits a random slice of this. A deploy reaches the whole floor in the same
// second, and a floor that leaves the pool together leaves in-flight dials with nobody to
// route to. Spreading them costs the same SDR-minutes but never empties a pool at once.
export const BANNER_JITTER_MAX_MS = 4 * 60_000;

// A version count from a JSON number or the string Vite inlines. Rejects anything that
// isn't a whole, non-negative count so a malformed value reads as "unknown" rather than
// ordering wrongly against a real one.
function toCount(value: unknown): number | null {
  // '' must not slip through: Number('') is 0, a perfectly valid count that would order
  // below every published one and mark a dev build permanently stale.
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = typeof value === 'string' ? Number(value.trim()) : value;
  if (typeof n !== 'number' || !Number.isSafeInteger(n) || n < 0) return null;
  return n;
}

// Slow on purpose. The banner is a nudge, not an alarm, and a background tab's timers
// are throttled to roughly a minute anyway; the visibility check is what makes it feel
// immediate when the SDR actually looks at the tab.
export const VERSION_POLL_MS = 5 * 60_000;

// Read the published sha out of whatever /version.json returned. Anything unexpected —
// the SPA fallback's HTML, a truncated body, a missing field — reads as "unknown".
export function parsePublishedSha(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const sha = (payload as { sha?: unknown }).sha;
  return typeof sha === 'string' && sha.trim() !== '' ? sha.trim() : null;
}

// Everything /version.json says. `sha` is for reading in a bucket and for bundles built
// before `n` existed; `n` is what the check below actually uses.
export interface PublishedVersion {
  sha: string | null;
  n: number | null;
  tier: ReloadTier;
}

// An unrecognised tier reads as `routine`. The tiers are a policy that will gain members;
// a bundle that meets one it does not know must stay quiet rather than guess loudly.
export function parseVersion(payload: unknown): PublishedVersion {
  const tier =
    typeof payload === 'object' && payload !== null && (payload as { tier?: unknown }).tier
      ? (payload as { tier: unknown }).tier
      : null;
  return {
    sha: parsePublishedSha(payload),
    n:
      typeof payload === 'object' && payload !== null
        ? toCount((payload as { n?: unknown }).n)
        : null,
    tier: tier === 'wanted' ? 'wanted' : 'routine',
  };
}

// An ORDERING, not an inequality: only a version STRICTLY ahead of ours is news.
//
// The version can legitimately move backwards. A `frontend:reload` deploy forces the
// count to the deploy commit's; the next deploy computes it from the SPA's own inputs
// again, which is an earlier commit. Under an inequality test every tab that loaded the
// forced bundle would be marked stale a second time, and the whole floor would be told to
// bounce one deploy after it already had.
//
// Falls back to the sha only when WE have no count — a bundle built before `n` existed,
// reading a version.json that has one. The reverse (we have a count, the payload does not)
// is a publisher we do not recognise, and the shas cannot decide it: ours is the deploy
// commit, the published one is the commit that last touched this SPA, and those routinely
// differ. Comparing them would report stale forever, on a banner no reload could clear.
//
// A dev build (no BUILD_SHA) and an unreadable version.json both still mean "no news".
export function isStale(
  running: { sha: string | null; n: number | null },
  published: PublishedVersion,
): boolean {
  if (running.n != null) return published.n != null && published.n > running.n;
  if (!running.sha || !published.sha) return false;
  return running.sha !== published.sha;
}

// How long to sit on the banner once staleness is known.
//
// Measured from DETECTION, never from build time: build time is the age of the last change
// to this bundle's inputs, routinely weeks, so a freshly-stale tab would already be past
// the limit and the whole floor would banner at once — the very thing the tier exists to
// stop. Every path is jittered for the same reason; detection is deploy-synchronised too.
export function bannerDelayMs(tier: ReloadTier, jitter: number): number {
  return (tier === 'wanted' ? 0 : STALE_AGE_LIMIT_MS) + jitter;
}

// What we are asking the SDR to do, given what they are in the middle of. A refresh
// drops the WebRTC leg, so this never tells someone on a call to do it now.
//
// `seatHeld` is the server's suspend-on-disconnect capability: with it, a plain
// refresh keeps the seat (the core holds it across the socket loss and the
// reconnect resumes), so the leave-first instruction — which on 03 Aug sent four
// SDRs through leave→refresh→rejoin onto a draining node for 7–9 silent minutes
// — is exactly the wrong advice. Without it (older or flag-off server) the old
// advice stands: a bare refresh would cost the seat.
export function refreshHint(onCall: boolean, inPool: boolean, seatHeld = false): string {
  if (onCall) return 'Finish this call first, then refresh this page.';
  if (inPool) {
    return seatHeld
      ? 'Refresh this page when you get a moment — your place in the pool is kept.'
      : 'Leave the pool and refresh this page when you get a moment.';
  }
  return 'Refresh this page now — you are not on a call.';
}
